import { useRef, useState } from 'react'

/**
 * iOS-style swipe-left to reveal Delete, no library. Touch-only by design —
 * desktop keeps its explicit delete buttons. Horizontal drags move the row;
 * vertical drags are left alone so the list still scrolls. While the row is
 * open (or just after a drag), taps close it instead of navigating.
 *
 * `enabled={false}` renders the children as a plain row — for accounts
 * that may see the list but not delete this item.
 */
const REVEAL = 88 // px of red button revealed by a full swipe

export default function SwipeRow({ onDelete, enabled = true, children }) {
  if (!enabled) return children
  return <SwipeRowInner onDelete={onDelete}>{children}</SwipeRowInner>
}

function SwipeRowInner({ onDelete, children }) {
  const [dx, setDx] = useState(0)
  const [dragging, setDragging] = useState(false)
  const start = useRef(null)
  const open = dx !== 0

  function onTouchStart(e) {
    const t = e.touches[0]
    start.current = { x: t.clientX, y: t.clientY, dx, horizontal: null }
  }

  function onTouchMove(e) {
    const s = start.current
    if (!s) return
    const t = e.touches[0]
    const moveX = t.clientX - s.x
    const moveY = t.clientY - s.y
    if (s.horizontal === null) {
      if (Math.abs(moveX) < 8 && Math.abs(moveY) < 8) return
      s.horizontal = Math.abs(moveX) > Math.abs(moveY)
      if (s.horizontal) setDragging(true)
    }
    if (!s.horizontal) return
    setDx(Math.max(-REVEAL, Math.min(0, s.dx + moveX)))
  }

  function onTouchEnd() {
    if (start.current?.horizontal) {
      setDx((d) => (d < -REVEAL / 2 ? -REVEAL : 0))
      // let the click that ends this gesture be swallowed first
      setTimeout(() => setDragging(false), 50)
    } else {
      setDragging(false)
    }
    start.current = null
  }

  function onClickCapture(e) {
    if (dragging || open) {
      e.preventDefault()
      e.stopPropagation()
      setDx(0)
    }
  }

  return (
    <div className="relative overflow-hidden" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      <button
        type="button"
        onClick={() => {
          setDx(0)
          onDelete()
        }}
        tabIndex={open ? 0 : -1}
        aria-hidden={!open}
        className="absolute inset-y-0 right-0 flex items-center justify-center bg-danger text-xs font-semibold text-white"
        style={{ width: REVEAL }}
      >
        Delete
      </button>
      <div
        onClickCapture={onClickCapture}
        className="relative bg-white"
        style={{ transform: `translateX(${dx}px)`, transition: dragging ? 'none' : 'transform .18s ease' }}
      >
        {children}
      </div>
    </div>
  )
}
